/**
 * widgets/BatchActions.ts · 通用批量操作 (复用 v200 TN_BATCH_ACTIONS)
 *
 * 场景:
 *   - 列表加 checkbox 列 + 顶部黄色 toolbar (统计已选 + 一键 action)
 *   - 替代逐行 click action 5-10x 工时节省
 *
 * 用法:
 *   import { renderBatchTable } from '@widgets/BatchActions';
 *   const table = renderBatchTable({
 *     columns: [...],
 *     data: rows,
 *     idKey: 'id',
 *     actions: [
 *       { key: 'approve', label: '✅ 批量批准', color: 'green', handler: async (ids) => {...} },
 *       { key: 'reject', label: '❌ 批量驳回', color: 'red', handler: async (ids) => {...}, confirm: '确定驳回?' },
 *     ],
 *   });
 */

import { renderDataTable, type Column } from './DataTable';
import { toast } from '@services/toast';

export interface BatchAction<T> {
  key: string;
  label: string;
  color?: 'blue' | 'green' | 'red' | 'orange' | 'gray';
  confirm?: string;  // 若有 · 先 confirm
  threshold?: number;  // 若有 · 选中数 ≥ threshold 才能用
  handler: (ids: number[], rows: T[]) => Promise<{ ok: number; fail: number; message?: string }> | void;
}

export interface BatchTableOpts<T> {
  columns: Column<T>[];
  data: T[];
  idKey?: keyof T;  // 默认 'id'
  actions: BatchAction<T>[];
  emptyText?: string;
  onAfterAction?: () => void;  // action 完后回调 (通常 rerender)
}

const COLORS = {
  blue: 'bg-blue-600 hover:bg-blue-700',
  green: 'bg-green-600 hover:bg-green-700',
  red: 'bg-red-600 hover:bg-red-700',
  orange: 'bg-orange-500 hover:bg-orange-600',
  gray: 'bg-gray-500 hover:bg-gray-600',
};

export function renderBatchTable<T extends { id?: number | string }>(opts: BatchTableOpts<T>): HTMLElement {
  const c = document.createElement('div');
  const idKey = (opts.idKey || 'id') as keyof T;
  const selected = new Set<number>();

  // Toolbar (sticky)
  const toolbar = document.createElement('div');
  toolbar.className = 'tn-batch-toolbar flex items-center gap-2 px-3 py-2 bg-yellow-50 border-b border-yellow-200 rounded-t-lg sticky top-0 z-10';
  toolbar.style.display = 'none';
  toolbar.innerHTML = `
    <span class="text-sm text-yellow-900 font-semibold">
      已选 <span class="tn-batch-count">0</span> 项
    </span>
    <span class="flex-1"></span>
    <div class="tn-batch-actions flex items-center gap-2"></div>
    <button class="tn-batch-clear text-xs text-gray-600 hover:text-red-600 px-2 py-1">清除</button>
  `;
  c.appendChild(toolbar);

  // 注入 actions 按钮
  const actionsHost = toolbar.querySelector('.tn-batch-actions') as HTMLElement;
  opts.actions.forEach((a) => {
    const btn = document.createElement('button');
    btn.className = `tn-batch-action-btn px-3 py-1.5 rounded text-white text-sm font-medium ${COLORS[a.color || 'blue']}`;
    btn.dataset.action = a.key;
    btn.textContent = a.label;
    btn.addEventListener('click', async () => {
      const ids = Array.from(selected);
      if (a.threshold && ids.length < a.threshold) {
        toast.warn(`请至少选 ${a.threshold} 项`);
        return;
      }
      if (a.confirm && !confirm(a.confirm + `\n\n已选 ${ids.length} 项`)) return;

      const rows = opts.data.filter((r) => ids.includes(Number((r as any)[idKey])));
      btn.disabled = true;
      try {
        const result = await a.handler(ids, rows);
        if (result) {
          if (result.fail === 0) {
            toast.success(`✅ ${a.label} 完成 · ${result.ok} 项${result.message ? ` · ${result.message}` : ''}`);
          } else {
            toast.warn(`⚠️ ${result.ok} 项成功 · ${result.fail} 项失败${result.message ? ` · ${result.message}` : ''}`);
          }
        }
        selected.clear();
        if (opts.onAfterAction) opts.onAfterAction();
      } catch (err) {
        toast.error(`❌ ${a.label} 失败 · ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        btn.disabled = false;
      }
    });
    actionsHost.appendChild(btn);
  });

  // 清除按钮
  toolbar.querySelector('.tn-batch-clear')?.addEventListener('click', () => {
    selected.clear();
    updateToolbar();
    c.querySelectorAll<HTMLInputElement>('.tn-batch-cb').forEach(cb => { cb.checked = false; });
    const headCb = c.querySelector<HTMLInputElement>('.tn-batch-head-cb');
    if (headCb) headCb.checked = false;
  });

  // 加 checkbox 列到 columns 头
  const checkboxCol: Column<T> = {
    key: '__batch__' as any,
    label: `<input type="checkbox" class="tn-batch-head-cb" />`,
    width: '40px',
    align: 'center',
    render: (r) => {
      const id = (r as any)[idKey];
      return `<input type="checkbox" class="tn-batch-cb" data-id="${id}" />`;
    },
  };

  const augmentedCols = [checkboxCol, ...opts.columns];
  const tableEl = renderDataTable<T>({
    columns: augmentedCols,
    data: opts.data,
    emptyText: opts.emptyText || '无数据',
  });
  c.appendChild(tableEl);

  // 事件代理 · checkbox 变化
  setTimeout(() => {
    const headCb = c.querySelector<HTMLInputElement>('.tn-batch-head-cb');
    headCb?.addEventListener('change', () => {
      const all = c.querySelectorAll<HTMLInputElement>('.tn-batch-cb');
      all.forEach((cb) => {
        cb.checked = headCb.checked;
        const id = parseInt(cb.dataset.id || '0');
        if (headCb.checked) selected.add(id);
        else selected.delete(id);
      });
      updateToolbar();
    });

    c.querySelectorAll<HTMLInputElement>('.tn-batch-cb').forEach((cb) => {
      cb.addEventListener('change', () => {
        const id = parseInt(cb.dataset.id || '0');
        if (cb.checked) selected.add(id);
        else selected.delete(id);
        updateToolbar();
      });
    });
  }, 0);

  function updateToolbar(): void {
    const n = selected.size;
    const countEl = toolbar.querySelector('.tn-batch-count');
    if (countEl) countEl.textContent = String(n);
    toolbar.style.display = n > 0 ? 'flex' : 'none';
  }

  return c;
}
